import { AgGridSolidRef } from '@ag-grid-community/solid';
import {
  SchedulingMethodFields,
  type SchedulingMethodValue,
} from '@components/FacilityDetails/SchedulingMethodFields';
import { WeeklyOperatingHoursEditor } from '@components/FacilityDetails/WeeklyOperatingHoursEditor';
import {
  createFacilityDetails,
  FacilityDetails,
  normalizeFacilityDetails,
  facilityScheduleOrderError,
  schedulingEmailCcError,
  serializeFacilityDetails,
} from '@common/facilityDetails';
import {
  Box,
  Checkbox,
  CollapsibleCard,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  Button,
  Notification,
  PhoneInput,
  SelectInput,
  ToastType,
} from '@components';
import AddressAutoComplete from '@components/Autocomplete/AddressAutoComplete';
import { FABInput } from '@components/FABInput';
import { ProductFormSkeleton } from '@components/Product/components';
import { FeatureFlags } from '@constants/featureFlags';
import { TimePicker } from '@components/TimePicker';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import { useParams } from '@solidjs/router';
import { createAppStore } from '@store/createAppStore';
import {
  AccessorialsType,
  AddressAccessorial,
  AddressBook,
  AddressBookModel,
  addressBookStore,
  editAddress,
  fetchAddressBookById,
  postAddress,
  setAddressBookStore,
  validateFacilityAddress,
} from '@store/customers/customerDetails/addressBookTab';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { fetchLegacyLtlAccessorials } from '@store/ltl/services';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { handleToast, printLog } from '@utils/utils';
import { ItemAccessorial } from '@views/ltl/types';
import { splitSuggestedStreet } from '@views/creditApplications/addressVerdict';
import { get, omit, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import {
  Accessor,
  createEffect,
  createSignal,
  For,
  Setter,
  Show,
} from 'solid-js';
import { produce } from 'solid-js/store';

import { countryList } from '../constants';
import { defaultOriginItem } from './AddressBookGrid';
import { addOrEditAddressStyle as classes } from './classes';
import { addressBookSchema } from './validation';

// Matches an existing address's accessorial (from the backend) against a
// selector item's accessorialId, which may be keyed on either the legacy GUID
// or the new LTL schema's numeric catalog id depending on the
// LtlAccessorialsSchemaRefactorRead feature flag.
const accessorialIdsMatch = (
  candidate: AddressAccessorial,
  selectorAccessorialId: string,
): boolean => {
  if (candidate.accessorialId === selectorAccessorialId) {
    return true;
  }
  return (
    candidate.ltlAccessorialId !== undefined &&
    candidate.ltlAccessorialId !== null &&
    String(candidate.ltlAccessorialId) === selectorAccessorialId
  );
};

type InitialAddressFormValues = {
  name: string;
  description: string | null;
  address1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  countryCode: string;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  openTimeFrom: string;
  openTimeTo: string;
  specialInstructions: string | null;
  drivingDirections: string | null;
  internalNote: string | null;
  isDefaultAddress: boolean;
  customerId: number;
  pickupHoursFrom: string;
  pickupHoursTo: string;
  pickupHoursClosed: boolean;
  pickupHoursNotes: string;
  receivingHoursFrom: string;
  receivingHoursTo: string;
  receivingHoursClosed: boolean;
  receivingUsePickupHours: boolean;
  receivingHoursNotes: string;
  appointmentType: string;
  schedulingMethod: string;
  facilityDetails: FacilityDetails;
};

type AddressDetails = {
  address1?: string;
  city?: string;
  county?: string;
  state?: string;
  zipCode?: string;
  country?: string;
};
type SuggestedAddress = {
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
};

type AddOrEditAddressBookProps = {
  modalId?: string;
  handleClose: () => void;
  edit: Accessor<boolean>;
  setEdit: Setter<boolean>;
  selectedRow: Accessor<AddressBookModel | null>;
  customerId?: number;
  gridRef: Accessor<AgGridSolidRef | null>;
  setSelectedRow: Setter<AddressBookModel | null>;
  triggerAddUpdateFn: Accessor<'add' | 'update' | 'delete' | 'none'>;
  setTriggerAddUpdateFn: Setter<'add' | 'update' | 'delete' | 'none'>;
};

const AddOrEditAddressBook = (props: AddOrEditAddressBookProps) => {
  const params = useParams();
  const [accessorials, setAccessorials] = createAppStore<AccessorialsType>(
    {
      pickup: [],
      delivery: [],
    },
    'addressBookAccessorials',
    true,
  );
  const [isSaveAndNew, setIsSaveAndNew] = createSignal(false);
  const [checked, setChecked] = createSignal('');
  const [isLoading, setIsLoading] = createSignal(true);
  const [loadedKey, setLoadedKey] = createSignal<string | null>(null);
  const [isVerifyingAddress, setIsVerifyingAddress] = createSignal(false);
  const [addressVerificationStatus, setAddressVerificationStatus] =
    createSignal<'unverified' | 'verified' | 'needs-review'>('unverified');
  const [addressVerificationMessage, setAddressVerificationMessage] =
    createSignal('Not verified yet.');
  const [suggestedAddress, setSuggestedAddress] =
    createSignal<SuggestedAddress>();
  let addressVerificationRequest = 0;
  const clearAddressVerification = () => {
    addressVerificationRequest += 1;
    setIsVerifyingAddress(false);
    setAddressVerificationStatus('unverified');
    setAddressVerificationMessage('Not verified yet.');
    setSuggestedAddress(undefined);
  };
  const [defaultOriginRow, setDefaultOriginRow] =
    createSignal<AddressBookModel | null>(null);
  // Always the legacy GUID-keyed accessorial catalog. Saving an address's
  // accessorials (PUT /api/CustomerAddress) requires a Guid accessorialId and
  // isn't aware of the new LTL schema's numeric-only catalog, so this must
  // stay independent of the shared, feature-flag-aware accessorialStore.
  const [accessorialCatalog, setAccessorialCatalog] = createSignal<
    ItemAccessorial[]
  >([]);

  const {
    form,
    errors,
    setIsDirty,
    isSubmitting,
    setIsSubmitting,
    setFields,
    setData,
    reset,
    data,
  } = createForm<InitialAddressFormValues>({
    initialValues: {
      name: '',
      description: null,
      address1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      countryCode: '',
      contactName: null,
      contactPhone: null,
      contactEmail: null,
      openTimeFrom: '',
      openTimeTo: '',
      specialInstructions: null,
      drivingDirections: null,
      internalNote: null,
      isDefaultAddress: false,
      pickupHoursFrom: '',
      pickupHoursTo: '',
      pickupHoursClosed: false,
      pickupHoursNotes: '',
      receivingHoursFrom: '',
      receivingHoursTo: '',
      receivingHoursClosed: false,
      receivingUsePickupHours: false,
      receivingHoursNotes: '',
      appointmentType: '',
      schedulingMethod: '',
      facilityDetails: createFacilityDetails(),
      customerId: Boolean(props.customerId)
        ? Number(props.customerId)
        : Number(params.id),
    },
    extend: validator({ schema: addressBookSchema }),
    onSubmit: async (values) => {
      const cb = (message: string) => {
        handleToast(ToastType.Success, message);
        reset();
        clearAddressVerification();
        setAddressBookStore('addOrEditErrorMessage', '');
      };
      setIsSubmitting(true);
      try {
        const commonPayload = {
          ...values,
          addressLine1: values.address1,
          facilityDetails: isFeatureFlagEnabled(
            FeatureFlags.AddressBookFacilityDetails,
          )()
            ? serializeFacilityDetails({
                ...values.facilityDetails,
                appointmentType: values.appointmentType,
                schedulingMethod: values.schedulingMethod,
              })
            : null,
        };
        const id = props.selectedRow()?.id;

        if (values.contactEmail != null && values.contactEmail !== '') {
          const isValidEmail =
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
              values.contactEmail,
            );
          if (!isValidEmail) {
            handleToast(ToastType.Error, 'Please enter a valid email address');
            return;
          }
        }

        if (isFeatureFlagEnabled(FeatureFlags.AddressBookFacilityDetails)()) {
          const ccError = schedulingEmailCcError(
            values.facilityDetails.schedulingEmailCc,
            true,
          );
          if (ccError) {
            handleToast(ToastType.Error, ccError);
            return;
          }
          const hoursError = facilityScheduleOrderError(values.facilityDetails);
          if (hoursError) {
            handleToast(ToastType.Error, hoursError);
            return;
          }
        }

        if (props.edit() && Boolean(id)) {
          const payload = {
            ...commonPayload,
            id,
            createdOn: props.selectedRow()?.createdOn,
            openTimeFrom: formatTimeToPayload(commonPayload.openTimeFrom),
            openTimeTo: formatTimeToPayload(commonPayload.openTimeTo),
            pickupHoursFrom: commonPayload.pickupHoursFrom
              ? formatTimeToPayload(commonPayload.pickupHoursFrom)
              : null,
            pickupHoursTo: commonPayload.pickupHoursTo
              ? formatTimeToPayload(commonPayload.pickupHoursTo)
              : null,
            receivingHoursFrom: commonPayload.receivingHoursFrom
              ? formatTimeToPayload(commonPayload.receivingHoursFrom)
              : null,
            receivingHoursTo: commonPayload.receivingHoursTo
              ? formatTimeToPayload(commonPayload.receivingHoursTo)
              : null,
            customerAddressAccessorials: [
              ...getCheckedAccessorials(accessorials.pickup),
              ...getCheckedAccessorials(accessorials.delivery),
            ] as AddressAccessorial[],
          };
          const res = await editAddress({ ...payload, cb } as AddressBook);
          if (res) {
            props.setSelectedRow(res);
            props.setTriggerAddUpdateFn('update');
            if (Boolean(isSaveAndNew())) {
              props.setEdit(false);
              props.setSelectedRow(null);
              setChecked('');
              initialSetAccessorials();
              reset();
            } else {
              props.handleClose();
            }
          }
        } else {
          const addPayload = {
            ...commonPayload,
            openTimeFrom: commonPayload.openTimeFrom
              ? formatTimeToPayload(commonPayload.openTimeFrom)
              : null,
            openTimeTo: commonPayload.openTimeTo
              ? formatTimeToPayload(commonPayload.openTimeTo)
              : null,
            pickupHoursFrom: commonPayload.pickupHoursFrom
              ? formatTimeToPayload(commonPayload.pickupHoursFrom)
              : null,
            pickupHoursTo: commonPayload.pickupHoursTo
              ? formatTimeToPayload(commonPayload.pickupHoursTo)
              : null,
            receivingHoursFrom: commonPayload.receivingHoursFrom
              ? formatTimeToPayload(commonPayload.receivingHoursFrom)
              : null,
            receivingHoursTo: commonPayload.receivingHoursTo
              ? formatTimeToPayload(commonPayload.receivingHoursTo)
              : null,
            customerAddressAccessorials: [
              ...getCheckedAccessorials(accessorials.pickup),
              ...getCheckedAccessorials(accessorials.delivery),
            ] as AddressAccessorial[],
          };
          const res = await postAddress({ ...addPayload, cb } as AddressBook);
          if (res) {
            props.setSelectedRow(res.value);
            props.setTriggerAddUpdateFn('add');
            if (Boolean(isSaveAndNew())) {
              props.setEdit(false);
              props.setSelectedRow(null);
              setChecked('');
              initialSetAccessorials();
              reset();
            } else props.handleClose();
          }
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        throw new Error(`Failed to Add Address: ${errorMessage}`);
      } finally {
        setIsSubmitting(false);
      }
    },
    onError: (errors) => {
      printLog(errors);
    },
  });

  const initialSetAccessorials = () => {
    setAccessorials({
      pickup: accessorials.pickup.map((x) => ({
        ...x,
        checked: false,
        operationType: 'None',
      })),
      delivery: accessorials.delivery.map((x) => ({
        ...x,
        checked: false,
        operationType: 'None',
      })),
    });
  };

  const formatTimeToPayload = (timeVal: string) => {
    return DateTime.fromISO(timeVal).toFormat('HH:mm:ss');
  };

  const formatTime = (timeVal: string) => {
    if (!timeVal) return '';
    const dateString = DateTime.now().toISODate();
    const res = DateTime.fromISO(`${dateString}T${timeVal}`).toISO();
    return res;
  };
  const applyLoadedAddress = (address: AddressBookModel) => {
    setData('name', get(address, 'name', ''));
    setData('description', get(address, 'description', null));
    setData('address1', get(address, 'addressLine1', ''));
    const addressLine2 = get(address, 'addressLine2');
    setData(
      'addressLine2',
      typeof addressLine2 === 'string' ? addressLine2 : '',
    );
    setData('city', get(address, 'city', ''));
    setData('state', get(address, 'state', ''));
    setData('zip', get(address, 'zip', ''));
    setData('countryCode', get(address, 'countryCode', ''));
    setData('contactName', get(address, 'contactName') as string);
    setData('contactPhone', get(address, 'contactPhone', null));
    setData('contactEmail', get(address, 'contactEmail') as string);
    setData('openTimeFrom', formatTime(get(address, 'openTimeFrom', '')) ?? '');
    setData('openTimeTo', formatTime(get(address, 'openTimeTo', '')) ?? '');
    setData('internalNote', get(address, 'internalNote') as string);
    setData(
      'specialInstructions',
      get(address, 'specialInstructions') as string,
    );
    setData('drivingDirections', get(address, 'drivingDirections') as string);
    setData('isDefaultAddress', get(address, 'isDefaultAddress') as boolean);
    setData(
      'pickupHoursFrom',
      formatTime(get(address, 'pickupHoursFrom', '') ?? '') ?? '',
    );
    setData(
      'pickupHoursTo',
      formatTime(get(address, 'pickupHoursTo', '') ?? '') ?? '',
    );
    setData('pickupHoursClosed', get(address, 'pickupHoursClosed') as boolean);
    setData('pickupHoursNotes', get(address, 'pickupHoursNotes') as string);
    setData(
      'receivingHoursFrom',
      formatTime(get(address, 'receivingHoursFrom', '') ?? '') ?? '',
    );
    setData(
      'receivingHoursTo',
      formatTime(get(address, 'receivingHoursTo', '') ?? '') ?? '',
    );
    setData(
      'receivingHoursClosed',
      get(address, 'receivingHoursClosed') as boolean,
    );
    setData(
      'receivingUsePickupHours',
      get(address, 'receivingUsePickupHours') as boolean,
    );
    setData(
      'receivingHoursNotes',
      get(address, 'receivingHoursNotes') as string,
    );
    setData('appointmentType', get(address, 'appointmentType') as string);
    setData('schedulingMethod', get(address, 'schedulingMethod') as string);
    const existingFacilityDetails = get(address, 'facilityDetails') as
      | FacilityDetails
      | undefined;
    setData(
      'facilityDetails',
      existingFacilityDetails
        ? normalizeFacilityDetails(existingFacilityDetails)
        : createFacilityDetails(),
    );
    const verificationStatus = get(address, 'addressVerificationStatus');
    if (verificationStatus === 'Verified') {
      setAddressVerificationStatus('verified');
      setAddressVerificationMessage('Address was verified when it was saved.');
    }
    const savedAccessorials = address.customerAddressAccessorials;
    const pickupAccessorials = savedAccessorials.filter(
      (x) => x.accessorialType === 1,
    );

    const deliveryAccessorials = savedAccessorials.filter(
      (x) => x.accessorialType === 2,
    );
    getAccessorialsInfo('pickup', pickupAccessorials);
    getAccessorialsInfo('delivery', deliveryAccessorials);
  };

  let loadRequest = 0;
  let activeKey = '';

  const requestedKey = () => {
    if (!props.edit()) return 'new';
    return `edit:${props.selectedRow()?.id ?? ''}`;
  };

  const formReady = () => !isLoading() && loadedKey() === requestedKey();

  createEffect(() => {
    const editing = props.edit();
    const id = editing ? props.selectedRow()?.id : undefined;
    const key = editing ? `edit:${id ?? ''}` : 'new';
    if (key === activeKey) return;
    activeKey = key;
    const requestId = ++loadRequest;
    setIsLoading(true);
    void Promise.resolve().then(async () => {
      const catalogPromise =
        accessorialCatalog().length === 0
          ? fetchLegacyLtlAccessorials().catch((error: unknown) => {
              handleToast(ToastType.Error, (error as Error).message);
              return [] as ItemAccessorial[];
            })
          : Promise.resolve(accessorialCatalog());
      const addressPromise = id
        ? fetchAddressBookById(id).catch((error: unknown) => {
            const fallback = props.selectedRow() ?? undefined;
            handleToast(
              ToastType.Caution,
              fallback
                ? 'Address details could not be refreshed. Editing the selected row.'
                : (error as Error).message,
            );
            return fallback;
          })
        : Promise.resolve(undefined);
      const [catalog, address] = await Promise.all([
        catalogPromise,
        addressPromise,
      ]);
      if (requestId !== loadRequest) return;
      if (accessorialCatalog().length === 0) {
        setAccessorialCatalog(catalog ?? []);
      }
      getAccessorialsInfo('Default');
      if (address != null) {
        applyLoadedAddress(address);
      } else if (!editing) {
        reset();
        clearAddressVerification();
        setChecked('');
      }
      setLoadedKey(key);
      setIsLoading(false);
    });
  });

  function getAccessorialsInfo(
    type: string,
    accessorialList?: AddressAccessorial[],
  ) {
    const pickupVals: AddressAccessorial[] = [];
    const deliveryVals: AddressAccessorial[] = [];

    accessorialCatalog().forEach((x) => {
      const item = {
        checked: false,
        accessorialId: x.id,
        accessorial: x,
        operationType: 'None',
      };

      if (Boolean(x.isPickup)) {
        pickupVals.push({ ...item, accessorialType: 1 });
      }

      if (Boolean(x.isDelivery)) {
        deliveryVals.push({ ...item, accessorialType: 2 });
      }
    });
    if (accessorialList) {
      let updatedAccessorials: AddressAccessorial[] = [];
      switch (type) {
        case 'pickup':
          updatedAccessorials = accessorials.pickup.map(
            (x: AddressAccessorial) => {
              const matchingAccessorial = accessorialList.find((a) =>
                accessorialIdsMatch(a, x.accessorialId),
              );
              if (matchingAccessorial) {
                return {
                  ...x,
                  ...matchingAccessorial,
                  checked: true,
                  operationType: 'None',
                };
              }
              return x;
            },
          );
          setAccessorials(
            'pickup',
            sortBy(updatedAccessorials, 'accessorial.name'),
          );
          break;
        case 'delivery':
          updatedAccessorials = accessorials.delivery.map(
            (x: AddressAccessorial) => {
              const matchingAccessorial = accessorialList.find((a) =>
                accessorialIdsMatch(a, x.accessorialId),
              );
              if (matchingAccessorial) {
                return {
                  ...x,
                  ...matchingAccessorial,
                  checked: true,
                  operationType: 'None',
                };
              }
              return x;
            },
          );
          setAccessorials(
            'delivery',
            sortBy(updatedAccessorials, 'accessorial.name'),
          );
          break;
        default:
          break;
      }
    } else {
      setAccessorials('pickup', sortBy(pickupVals, 'accessorial.name'));
      setAccessorials('delivery', sortBy(deliveryVals, 'accessorial.name'));
    }
  }

  const getCheckedAccessorials = (accessorials: AddressAccessorial[]) => {
    return accessorials
      .filter((x) => x.checked || x.operationType === 'Delete')
      .map((x) => omit(x, 'checked'));
  };

  const setAddressFields = (
    field: keyof InitialAddressFormValues,
    value: string | boolean | FacilityDetails,
  ) => {
    if (
      [
        'address1',
        'addressLine2',
        'city',
        'state',
        'zip',
        'countryCode',
      ].includes(field)
    ) {
      addressVerificationRequest += 1;
      setIsVerifyingAddress(false);
      setAddressVerificationStatus('unverified');
      setAddressVerificationMessage(
        'Address changed. Verify again when ready.',
      );
      setSuggestedAddress(undefined);
    }
    setIsDirty(true);
    setFields(field, value);
  };

  const applyAddressScheduling = (value: SchedulingMethodValue) => {
    setAddressFields('appointmentType', value.appointmentType);
    setAddressFields('schedulingMethod', value.schedulingMethod);
    setAddressFields('facilityDetails', {
      ...data().facilityDetails,
      appointmentType: value.appointmentType,
      schedulingMethod: value.schedulingMethod,
      schedulingMethodDetails: value.schedulingMethodDetails,
      schedulingEmailCc: value.schedulingEmailCc,
      portalUrl: value.portalUrl,
      portalCredentials: value.portalCredentials,
    });
  };

  const addressSnapshot = () => ({
    address1: data().address1.trim(),
    addressLine2: data().addressLine2.trim(),
    city: data().city.trim(),
    state: data().state.trim(),
    zip: data().zip.trim(),
    countryCode: data().countryCode,
  });

  const verifyAddress = async () => {
    const requestedAddress = addressSnapshot();
    const { address1, addressLine2, city, state, zip, countryCode } =
      requestedAddress;
    if (!address1 || !city || !state || !zip) {
      addressVerificationRequest += 1;
      setIsVerifyingAddress(false);
      setAddressVerificationStatus('needs-review');
      setAddressVerificationMessage(
        'Complete address, city, state, and ZIP before verification.',
      );
      setSuggestedAddress(undefined);
      return;
    }

    const requestId = ++addressVerificationRequest;
    const isCurrentRequest = () =>
      requestId === addressVerificationRequest &&
      address1 === data().address1.trim() &&
      addressLine2 === data().addressLine2.trim() &&
      city === data().city.trim() &&
      state === data().state.trim() &&
      zip === data().zip.trim() &&
      countryCode === data().countryCode;

    try {
      setIsVerifyingAddress(true);
      const response = await validateFacilityAddress({
        address: [address1, addressLine2].filter(Boolean).join(', '),
        city,
        state,
        postalCode: zip,
        countryCode,
      });
      if (!isCurrentRequest()) return;
      const isVerified = response.verdict?.validationStatus === 'Confirmed';
      setAddressVerificationStatus(isVerified ? 'verified' : 'needs-review');
      setAddressVerificationMessage(
        isVerified
          ? 'Address verified by service.'
          : (response.verdict?.validationMessages?.join(' ') ??
              'Address needs review. You can still save it.'),
      );
      setSuggestedAddress(response.validatedAddress);
    } catch {
      if (!isCurrentRequest()) return;
      setAddressVerificationStatus('needs-review');
      setAddressVerificationMessage(
        'Address verification is unavailable. You can still save this address.',
      );
      setSuggestedAddress(undefined);
    } finally {
      if (requestId === addressVerificationRequest) {
        setIsVerifyingAddress(false);
      }
    }
  };

  const applySuggestedAddress = () => {
    const address = suggestedAddress();
    if (address == null) return;
    const split = splitSuggestedStreet(
      address.address || data().address1,
      data().addressLine2,
    );
    const address1 = split.line1;
    const city = address.city || data().city;
    const state = address.state || data().state;
    const zip = address.postalCode || data().zip;
    const countryCode = address.countryCode || data().countryCode;
    setAddressFields('address1', address1);
    setAddressFields('addressLine2', split.line2);
    setAddressFields('city', city);
    setAddressFields('state', state);
    setAddressFields('zip', zip);
    const countryCodes: Record<string, string> = {
      US: 'USA',
      CA: 'CAN',
      MX: 'MEX',
    };
    setAddressFields('countryCode', countryCodes[countryCode] || countryCode);
    setSuggestedAddress(undefined);
    void verifyAddress();
  };

  const handleGooglePlacesSelect = (place: AddressDetails) => {
    const {
      address1 = '',
      city = '',
      country = '',
      state = '',
      zipCode = '',
    } = place;
    setAddressFields('address1', address1);
    setAddressFields('city', city);
    setAddressFields('state', state.slice(0, 2));
    setAddressFields('zip', zipCode);

    const countryCodes: Record<string, string> = {
      US: 'USA',
      CA: 'CAN',
      MX: 'MEX',
      'United States': 'USA',
      Canada: 'CAN',
      Mexico: 'MEX',
    };
    setAddressFields('countryCode', countryCodes[country] ?? '');
  };

  const setAddressDefaultText = () => {
    const defaultOriginItemFromProps = defaultOriginItem().find(
      (item: AddressBookModel) => item.isDefaultAddress === true,
    );
    setDefaultOriginRow(defaultOriginItemFromProps as AddressBookModel);
    const selectedAddress = props.selectedRow();
    if (!selectedAddress) {
      if (checked() === 'Yes') {
        const message = (
          <Typography variant="body2" class="text-red-500">
            {Boolean(defaultOriginRow())
              ? `Clicking save will change the default origin from ${
                  defaultOriginRow()?.addressLine1
                } ${defaultOriginRow()?.city}, ${defaultOriginRow()?.state} ${
                  defaultOriginRow()?.zip
                } to ${data().address1} ${data().city}, ${
                  data().state
                } ${data().zip}`
              : `Clicking Save will default origin to ${data().address1} ${
                  data().city
                }, ${data().state} ${data().zip}`}
          </Typography>
        );
        return message;
      }
      return null;
    }

    if (checked() === 'No' && selectedAddress.isDefaultAddress) {
      return (
        <Typography variant="body2" class="text-red-500">
          Clicking save will remove {data().address1} {data().city},{' '}
          {data().state} {data().zip} as the default origin
        </Typography>
      );
    } else if (checked() === 'Yes' && !selectedAddress.isDefaultAddress) {
      return (
        <Typography variant="body2" class="text-red-500">
          {defaultOriginRow()
            ? `Clicking save will change the default origin from ${
                defaultOriginRow()?.addressLine1
              } ${defaultOriginRow()?.city}, ${defaultOriginRow()?.state} ${
                defaultOriginRow()?.zip
              } to ${data().address1} ${data().city}, ${data().state} ${
                data().zip
              }`
            : `Clicking Save will default origin to ${data().address1} ${
                data().city
              }, ${data().state} ${data().zip}`}
        </Typography>
      );
    }

    return null;
  };

  const handleAccessorialChange = (
    type: 'pickup' | 'delivery' | 'shipment',
    i: Accessor<number>,
  ) => {
    if (type === 'pickup' || type === 'delivery') {
      setAccessorials(
        type,
        produce((items) => {
          items.forEach((item, index) => {
            if (index === i()) {
              item.checked = !item.checked;
              switch (item.operationType) {
                case 'None':
                  if (item.checked) {
                    item.operationType = 'Insert';
                  } else {
                    item.operationType = 'Delete';
                  }
                  break;
                case 'Add':
                  item.operationType = 'None';
                  break;
                case 'Delete':
                  item.operationType = 'None';
                  break;
                default:
              }
            }
          });
        }),
      );
    }
  };

  return (
    <form ref={form} class={classes.rowFormGrid}>
      <Show when={formReady()} fallback={<ProductFormSkeleton />}>
        <Box style={{ 'flex-grow': 1 }}>
          <Show when={addressBookStore.addOrEditErrorMessage}>
            <Grid container item size={12} spacing={2} class={classes.errorBox}>
              <Grid item size={12}>
                <Notification
                  type="error"
                  text={addressBookStore.addOrEditErrorMessage}
                />
              </Grid>
            </Grid>
          </Show>
          <Grid container spacing={2} gap={2}>
            <Grid item size={12}>
              <Typography variant="body1">Location Details</Typography>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }} class="md:order-1">
              <Grid container gap={2} rowSpacing={2}>
                <Grid item size={12}>
                  <TextField
                    name="name"
                    label="Location Name"
                    error={errors().name}
                    value={data().name}
                    onChange={(itm: string) => setAddressFields('name', itm)}
                    maxLength={150}
                    dataTestId="address-book-location-name-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <TextField
                    name="description"
                    label="Location Description"
                    error={errors().description}
                    value={data().description ?? ''}
                    onChange={(itm: string) =>
                      setAddressFields('description', itm)
                    }
                    maxLength={100}
                    dataTestId="address-book-location-description-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <Typography variant="body1">Address</Typography>
                </Grid>
                <Grid item size={12}>
                  <AddressAutoComplete
                    label="Address1"
                    placeholder="Enter Address"
                    value={data().address1}
                    onChange={(itm) => {
                      setAddressFields('address1', itm);
                    }}
                    onItemSelect={(item) => {
                      handleGooglePlacesSelect(
                        item as unknown as AddressDetails,
                      );
                    }}
                    zIndex="9999"
                    error={errors().address1}
                    maxLength={100}
                  />
                </Grid>
                <Grid item size={12}>
                  <TextField
                    label="Address2"
                    placeholder="Address line 2"
                    error={errors().addressLine2}
                    value={data().addressLine2}
                    onChange={(itm: string) =>
                      setAddressFields('addressLine2', itm)
                    }
                    maxLength={100}
                    dataTestId="address-book-address2-textInput"
                  />
                </Grid>
                <Grid item>
                  <Stack direction="row" gap={2}>
                    <Box flex="1">
                      <TextField
                        label="City"
                        placeholder="City"
                        value={data().city}
                        onChange={(itm: string | number | Event | null) =>
                          setAddressFields('city', itm as string)
                        }
                        error={errors().city}
                        maxLength={50}
                        dataTestId="address-book-city-textInput"
                      />
                    </Box>
                    <Box flex="1">
                      <TextField
                        label="State"
                        placeholder="State"
                        value={data().state}
                        onChange={(itm: string) => {
                          const sanitized = sanitizeStateInput(itm);
                          if (sanitized === data().state) return;
                          setAddressFields('state', sanitized);
                        }}
                        error={errors().state}
                        maxLength={2}
                        dataTestId="address-book-state-textInput"
                      />
                    </Box>
                    <Box flex="1">
                      <TextField
                        label="Zip"
                        placeholder="Pincode"
                        value={data().zip}
                        onChange={(value: string | number | Event | null) =>
                          setAddressFields('zip', value as string)
                        }
                        error={errors().zip}
                        dataTestId="address-book-zip-textInput"
                      />
                    </Box>
                  </Stack>
                  <Box width="100%" mt={2}>
                    <SelectInput
                      label="USA/CAN/MEX"
                      placeholder="Select Country"
                      menuItems={countryList.map((c) => ({
                        label: c.label,
                        value: String(c.value),
                      }))}
                      value={data().countryCode}
                      onChange={(val) => setAddressFields('countryCode', val)}
                      error={((): string | null => {
                        const err = errors().countryCode;
                        if (err == null) return null;
                        if (Array.isArray(err)) return err[0] ?? null;
                        return typeof err === 'string' ? err : null;
                      })()}
                      dataTestId={formatDataTestId(
                        'Address book country',
                        'selectInput',
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item size={12}>
                  <Box class="border border-gray-200 bg-gray-50 p-3">
                    <Stack
                      direction="row"
                      gap={2}
                      class="items-center justify-between"
                    >
                      <Box>
                        <Typography variant="body2">
                          Address Verification
                        </Typography>
                        <Typography variant="caption">
                          {addressVerificationMessage()}
                        </Typography>
                      </Box>
                      <Button
                        label="Verify"
                        variant="contained"
                        size="sm"
                        type="button"
                        isLoading={isVerifyingAddress()}
                        disabled={isVerifyingAddress()}
                        onClick={() => void verifyAddress()}
                        dataTestId="address-book-verify-address-button"
                      />
                    </Stack>
                    <Show when={suggestedAddress()}>
                      <Stack direction="row" gap={2} class="mt-2 items-center">
                        <Typography variant="caption">
                          A normalized address is available.
                        </Typography>
                        <button
                          type="button"
                          class="min-h-11 text-primary underline"
                          onClick={applySuggestedAddress}
                        >
                          Apply suggested address
                        </button>
                      </Stack>
                    </Show>
                    <Show when={addressVerificationStatus() === 'needs-review'}>
                      <Typography variant="caption" class="text-amber-700">
                        Review recommended. Saving remains available.
                      </Typography>
                    </Show>
                  </Box>
                </Grid>
                <Grid item size={12}>
                  <Typography variant="body1">Contact</Typography>
                </Grid>
                <Grid item size={12}>
                  <TextField
                    label="Name"
                    placeholder="Name"
                    name="contactName"
                    value={data().contactName as string}
                    onChange={(itm: string) =>
                      setAddressFields('contactName', itm)
                    }
                    error={errors().contactName}
                    dataTestId="address-book-contact-name-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <PhoneInput
                    label="Phone"
                    placeholder="Phone Number"
                    value={data().contactPhone as string}
                    onChange={(value) =>
                      setAddressFields('contactPhone', value)
                    }
                    error={errors().contactPhone}
                    dataTestId="address-book-contact-phone-phoneInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <TextField
                    label="Email"
                    placeholder="Email"
                    name="contactEmail"
                    value={data().contactEmail as string}
                    onChange={(itm: string) =>
                      setAddressFields('contactEmail', itm)
                    }
                    error={errors().contactEmail}
                    dataTestId="address-book-contact-email-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <Typography variant="body1">LTL Hours</Typography>
                </Grid>
                <Stack direction="row" width="100%" gap={2}>
                  <Box flex="1">
                    <TimePicker
                      label="From"
                      value={data().openTimeFrom}
                      onChange={(newTime) =>
                        setAddressFields('openTimeFrom', newTime)
                      }
                      dataTestId="address-book-hours-from-timePicker"
                    />
                  </Box>
                  <Box flex="1">
                    <TimePicker
                      label="To"
                      value={data().openTimeTo}
                      onChange={(newTime) =>
                        setAddressFields('openTimeTo', newTime)
                      }
                      dataTestId="address-book-hours-to-timePicker"
                    />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
            <Show
              when={isFeatureFlagEnabled(
                FeatureFlags.AddressBookFacilityDetails,
              )()}
            >
              <Grid item size={12} class="md:order-3">
                <div class="mb-2">
                  <Typography
                    variant="body1"
                    class="font-bold text-armstrong-primary"
                  >
                    Facility Details
                  </Typography>
                  <Typography variant="caption" class="mb-3 block text-primary">
                    Operating hours are informational only.
                  </Typography>
                  <SchedulingMethodFields
                    layout="inline"
                    appointmentLabel="Appointment Type *"
                    variant="address-book"
                    value={() => ({
                      appointmentType: data().appointmentType,
                      schedulingMethod: data().schedulingMethod,
                      schedulingMethodDetails:
                        data().facilityDetails.schedulingMethodDetails,
                      schedulingEmailCc:
                        data().facilityDetails.schedulingEmailCc,
                      portalUrl: data().facilityDetails.portalUrl,
                      portalCredentials:
                        data().facilityDetails.portalCredentials,
                    })}
                    onChange={applyAddressScheduling}
                  />
                  <div class="mt-4">
                    <WeeklyOperatingHoursEditor
                      facilityDetails={() => data().facilityDetails}
                      onChange={(facilityDetails) =>
                        setAddressFields('facilityDetails', facilityDetails)
                      }
                      dataTestIdPrefix="address-book-weekly-hours"
                    />
                  </div>
                </div>
              </Grid>
            </Show>
            <Grid item size={{ xs: 12, md: 6 }} class="md:order-2">
              <Grid container spacing={2} rowSpacing={2}>
                <Grid item size={12}>
                  <TextField
                    multiline
                    rows={5}
                    label="Location Instructions"
                    name="specialInstructions"
                    placeholder="Enter Location Instructions"
                    value={data().specialInstructions ?? ''}
                    onChange={(itm: string) =>
                      setAddressFields('specialInstructions', itm)
                    }
                    error={errors().specialInstructions}
                    maxLength={2000}
                    dataTestId="address-book-location-instructions-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <CollapsibleCard
                    startTitle="Accessorials"
                    expanded={false}
                    class="mb-2"
                  >
                    <Grid container spacing={4} rowSpacing={4}>
                      <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        class="min-w-0 overflow-hidden md:pr-2"
                      >
                        <Box
                          class={'flex min-w-0 flex-col gap-1 overflow-hidden'}
                        >
                          <Typography variant="body1">Pickup</Typography>
                          <For each={accessorials.pickup}>
                            {(pickup, i) => {
                              return (
                                <Grid item class="min-w-0 overflow-hidden">
                                  <FABInput
                                    label={pickup.accessorial.name!}
                                    checked={pickup.checked}
                                    color="primary"
                                    truncateLabel
                                    class="inline-flex max-w-full! overflow-hidden! items-center gap-1 text-left!"
                                    onClick={() =>
                                      handleAccessorialChange('pickup', i)
                                    }
                                  />
                                </Grid>
                              );
                            }}
                          </For>
                        </Box>
                      </Grid>
                      <Grid
                        item
                        size={{ xs: 12, md: 6 }}
                        class="min-w-0 overflow-hidden md:pl-2"
                      >
                        <Box
                          class={'flex min-w-0 flex-col gap-1 overflow-hidden'}
                        >
                          <Typography variant="body1">Delivery</Typography>
                          <For each={accessorials.delivery}>
                            {(delivery, i) => {
                              return (
                                <Grid item class="min-w-0 overflow-hidden">
                                  <FABInput
                                    label={delivery.accessorial.name!}
                                    checked={delivery.checked}
                                    color="primary"
                                    truncateLabel
                                    class="inline-flex max-w-full! overflow-hidden! items-center gap-1 text-left!"
                                    onClick={() =>
                                      handleAccessorialChange('delivery', i)
                                    }
                                  />
                                </Grid>
                              );
                            }}
                          </For>
                        </Box>
                      </Grid>
                    </Grid>
                  </CollapsibleCard>
                </Grid>
                <Grid item size={12}>
                  <TextField
                    multiline
                    rows={5}
                    label="Driving Directions"
                    name="drivingDirections"
                    placeholder="Enter Driving Directions"
                    value={data().drivingDirections ?? ''}
                    onChange={(itm: string) =>
                      setAddressFields('drivingDirections', itm)
                    }
                    error={errors().drivingDirections}
                    maxLength={2000}
                    dataTestId="address-book-driving-directions-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <TextField
                    multiline
                    rows={5}
                    label="Internal Notes"
                    name="internalNote"
                    placeholder="Enter Internal Notes"
                    value={data().internalNote ?? ''}
                    onChange={(itm: string) =>
                      setAddressFields('internalNote', itm)
                    }
                    error={errors().internalNote}
                    maxLength={2000}
                    dataTestId="address-book-internal-notes-textInput"
                  />
                </Grid>
                <Grid item size={12}>
                  <Checkbox
                    size="xs"
                    color="info"
                    class="ml-[-9px]"
                    label="Set as a Default Origin"
                    name="isDefaultAddress"
                    checked={data().isDefaultAddress}
                    onChange={(value) => {
                      setAddressFields('isDefaultAddress', value as boolean);
                      value ? setChecked('Yes') : setChecked('No');
                    }}
                    dataTestId="address-book-default-origin-checkboxInput"
                  />
                  <Grid item size={12}>
                    {setAddressDefaultText()}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              size={12}
              gap={2}
              display="flex"
              alignItems="center"
              justifyContent="end"
            >
              <Button
                isLoading={isSaveAndNew() && isSubmitting()}
                label="Save And Add New"
                variant="contained"
                type="submit"
                onClick={() => {
                  setIsSaveAndNew(true);
                }}
                sx={{
                  background: '#468DB5',
                  color: '#FFF',
                  'margin-right': '4px',
                }}
                disabled={isSubmitting()}
                dataTestId={formatDataTestId('Save And Add New', 'button')}
              />
              <Button
                isLoading={!isSaveAndNew() && isSubmitting()}
                label="Save"
                type="submit"
                variant="contained"
                onClick={() => {
                  setIsSaveAndNew(false);
                }}
                disabled={isSubmitting()}
                dataTestId={formatDataTestId('Save', 'button')}
              />
            </Grid>
          </Grid>
        </Box>
      </Show>
    </form>
  );
};

export default AddOrEditAddressBook;
